import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme, T } from '../context/ThemeContext';

const apiClient = axios.create({ baseURL: '/api', timeout: 10000 });
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('semsim_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
apiClient.interceptors.response.use(r => r, err => {
  // Every catch(e) block below (and there are many) just does alert(e.message)/setErr(e.message) -
  // by default that's axios's generic "Request failed with status code 400", useless to the user.
  // Rewrite it here, once, to the server's actual reason (express-validator's per-field errors,
  // or a plain {error}) so every existing call site starts showing something useful automatically.
  const data = err.response?.data;
  if (data?.errors?.length) err.message = data.errors.map(e => e.msg && e.msg !== 'Invalid value' ? e.msg : `${e.path}: ${e.msg}`).join(' | ');
  else if (data?.error) err.message = data.error;
  return Promise.reject(err);
});
const api = {
  get: (url) => apiClient.get(url).then(r => r.data),
  post: (url, data) => apiClient.post(url, data).then(r => r.data),
  put: (url, data) => apiClient.put(url, data).then(r => r.data),
  delete: (url) => apiClient.delete(url).then(r => r.data),
};

// site_manager ("סייר") only shows up correctly once given a real label here - previously any
// role other than cross_municipal just rendered its raw DB value (e.g. literal "moked_operator")
function roleLabel(role, lang) {
  const map = {
    superadmin: lang==='he'?'סופר-אדמין':'Superadmin',
    admin: lang==='he'?'מנהל לקוח':'Client Admin',
    moked_operator: lang==='he'?'מוקדן / גננת':'Operator',
    site_manager: lang==='he'?'סייר':'Patrol',
    cross_municipal: lang==='he'?'מנהל מוקד (הכל)':'Moked Manager (all)',
  };
  return map[role] || role;
}
function roleBadgeColors(role, theme) {
  if (role==='cross_municipal') return ['#7c3aed','#6d28d9'];
  if (role==='site_manager') return ['#f97316','#c2410c'];
  return [theme.tagSite[0], theme.tagSite[1]];
}

const Field = ({label,value,onChange,placeholder,type='text',theme}) => (
  <div style={{marginBottom:12}}>
    <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{label}</div>
    <input style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14,boxSizing:'border-box'}} type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||''} />
  </div>
);

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { dark, setDark, lang, setLang } = useTheme();
  const t = T[lang];
  const [tab, setTab] = useState('municipalities');

  const theme = dark ? {
    bg: '#0f1729', card: '#1a2744', header: '#1e2d4a', nav: '#162236',
    border: '#2d3f5e', text: '#e2e8f0', subtext: '#94a3b8',
    inputBg: '#0f1729', cardText: '#ffffff', tagSite: ['#185fa5','#93c5fd'],
    tagUser: ['#3b6d11','#86efac'], navActive: '#2d3f5e', deleteBtn: ['transparent','#ef4444','#ef4444'],
  } : {
    bg: '#dbeafe', card: '#bfdbfe', header: '#1d4ed8', nav: '#1e40af',
    border: '#93c3fd', text: '#1e3a5f', subtext: '#1e40af',
    inputBg: '#eff6ff', cardText: '#1e3a5f', tagSite: ['#1d4ed8','#bfdbfe'],
    tagUser: ['#065f46','#6ee7b7'], navActive: '#1e40af', deleteBtn: ['transparent','#dc2626','#dc2626'],
  };

  const S = {
    btn: (bg,color) => ({background:bg,color:color||'#fff',border:'none',borderRadius:6,padding:'6px 14px',cursor:'pointer',fontSize:13}),
    tag: (bg,border) => ({background:bg,border:`1px solid ${border}`,borderRadius:12,padding:'2px 10px',fontSize:12,color:'#fff'}),
    outBtn: {background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)',borderRadius:6,padding:'6px 14px',cursor:'pointer',fontSize:13},
    deleteBtn: {background:theme.deleteBtn[0],color:theme.deleteBtn[1],border:`1px solid ${theme.deleteBtn[2]}`,borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:12},
    card: {background:theme.card,border:`1px solid ${theme.border}`,borderRadius:8,marginBottom:12,overflow:'hidden'},
  };

  const TABS = [['municipalities',`🏛 ${t.municipalities}`],['sites',`🏗 ${t.sites}`],['history',`📋 ${t.history}`],['users',`👤 ${t.users}`],['devices',`📡 ${lang==='he'?'מכשירים':'Devices'}`]];

  return (
    <div style={{minHeight:'100vh',background:theme.bg,color:theme.text,fontFamily:'Heebo,sans-serif',direction:lang==='he'?'rtl':'ltr'}}>
      <div style={{background:theme.header,padding:'12px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:`1px solid ${theme.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <div style={{fontSize:20,fontWeight:800,color:'#fff'}}>SEM SIM</div>
          <div style={{fontSize:12,color:'#93c5fd',fontWeight:700}}>{t.adminPanel}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:13,color:'rgba(255,255,255,0.7)'}}>{user?.name}</span>
          <button onClick={()=>setLang(lang==='he'?'en':'he')} style={{...S.outBtn,fontWeight:700}}>{lang==='he'?'EN':'עב'}</button>
          <button onClick={()=>setDark(!dark)} style={S.outBtn}>{dark?'☀️':'🌙'}</button>
          <button onClick={()=>window.location.href='/moked'} style={S.outBtn}>{t.moked}</button>
          <button onClick={()=>window.location.href='/reports'}
            style={{...S.outBtn, color:'#a78bfa', borderColor:'#a78bfa'}}>
            📊 {lang==='he'?'דוחות':'Reports'}
          </button>
          <button onClick={()=>navigate('/profile')} style={{...S.outBtn,background:'transparent',border:`1px solid ${theme.border}`,marginRight:6}}>{lang==='he'?'פרופיל':'Profile'}</button><button onClick={logout} style={S.outBtn}>{t.logout}</button>
        </div>
      </div>
      <div style={{display:'flex',minHeight:'calc(100vh - 60px)'}}>
        <div style={{width:200,background:theme.nav,padding:'20px 0',borderLeft:lang==='he'?`1px solid ${theme.border}`:'none',borderRight:lang==='he'?'none':`1px solid ${theme.border}`}}>
          {TABS.map(([tv,l])=>(
            <button key={tv} onClick={()=>setTab(tv)} style={{display:'block',width:'100%',padding:'12px 20px',background:tab===tv?theme.navActive:'transparent',color:'#fff',border:'none',cursor:'pointer',textAlign:lang==='he'?'right':'left',fontSize:14,opacity:tab===tv?1:0.7}}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,padding:'24px'}}>
          {tab==='municipalities' && <MunicipalitiesTab theme={theme} S={S} t={t} lang={lang} user={user}/>}
          {tab==='sites'         && <SitesTab theme={theme} S={S} t={t}/>}
          {tab==='history'       && <HistoryTab theme={theme} S={S} t={t}/>}
          {tab==='users'         && <UsersTab theme={theme} S={S} t={t} lang={lang}/>}
          {tab==='devices'       && <DevicesTab theme={theme} S={S} t={t} lang={lang} user={user}/>}
        </div>
      </div>
    </div>
  );
}

function MunicipalitiesTab({theme,S,t,lang,user}) {
  const [munis,setMunis]=useState([]);
  const [expanded,setExpanded]=useState(null);
  const [showNew,setShowNew]=useState(false);
  const [form,setForm]=useState({name:'',city:''});
  const [editingId,setEditingId]=useState(null);
  const [editForm,setEditForm]=useState({name:'',city:''});
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState('');

  const load=async()=>{ try{const d=await api.get('/municipalities');setMunis(d);}catch(e){if(e?.response?.status!==403)setErr(t.loading);} };
  useEffect(()=>{load();},[]);

  const create=async()=>{
    setLoading(true);setErr('');
    try{await api.post('/municipalities',form);setForm({name:'',city:''});setShowNew(false);load();}
    catch(e){setErr(e.message);}
    setLoading(false);
  };

  const startEdit=(m)=>{ setEditingId(m.id); setEditForm({name:m.name||'',city:m.city||''}); setExpanded(null); };
  const saveEdit=async()=>{
    setLoading(true);setErr('');
    try{await api.put(`/municipalities/${editingId}`,editForm);setEditingId(null);load();}
    catch(e){setErr(e.message);}
    setLoading(false);
  };

  const deleteMuni=async(id,name)=>{
    if(!window.confirm(`${lang==='he'?'למחוק את':'Delete'} "${name}"?`))return;
    try{await api.delete(`/municipalities/${id}`);load();}
    catch(e){alert(e.message);}
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <h2 style={{margin:0,color:theme.text}}>{t.municipalities}</h2>
        <button style={S.btn('#3b82f6')} onClick={()=>setShowNew(!showNew)}>{t.newMuni}</button>
      </div>
      <div style={{color:theme.subtext,fontSize:13,marginBottom:16}}>{munis.length} {t.active}</div>
      {err&&<div style={{color:'#ef4444',marginBottom:12}}>{err}</div>}
      {showNew&&(
        <div style={{...S.card,padding:16,marginBottom:16}}>
          <Field theme={theme} label={t.muniName} value={form.name} onChange={v=>setForm(p=>({...p,name:v}))} placeholder={lang==='he'?'שם הלקוח...':'Client name...'}/>
          <Field theme={theme} label={t.city} value={form.city} onChange={v=>setForm(p=>({...p,city:v}))} placeholder={lang==='he'?'תל אביב...':'Tel Aviv...'}/>
          <div style={{display:'flex',gap:8}}>
            <button style={S.btn('#22c55e')} onClick={create} disabled={loading}>{loading?t.loading:t.save}</button>
            <button style={S.btn('#374151')} onClick={()=>setShowNew(false)}>{t.cancel}</button>
          </div>
        </div>
      )}
      {munis.map(m=>(
        <div key={m.id} style={S.card}>
          <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:10}}>
            <span onClick={()=>setExpanded(expanded===m.id?null:m.id)} style={{flex:1,display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
              <span style={{color:theme.subtext}}>{expanded===m.id?'▲':'▼'}</span>
              <span style={{fontSize:15,fontWeight:800,color:theme.cardText}}>🏛 {m.name}</span>
              {m.city&&<span style={{fontSize:12,color:theme.subtext}}>{m.city}</span>}
            </span>
            <span style={S.tag(theme.tagSite[0],theme.tagSite[1])}>{m.site_count||0} {t.sites}</span>
            <span style={{...S.tag(theme.tagUser[0],theme.tagUser[1]),margin:'0 6px'}}>{m.user_count||0} {t.users}</span>
            <button style={{...S.deleteBtn,color:'#3b82f6',borderColor:'#3b82f6'}} onClick={()=>startEdit(m)}>✏️ {lang==='he'?'עריכה':'Edit'}</button>
            <button style={S.deleteBtn} onClick={()=>deleteMuni(m.id,m.name)}>🗑 {t.delete}</button>
          </div>
          {editingId===m.id&&(
            <div style={{padding:16,borderTop:`1px solid ${theme.border}`}}>
              <Field theme={theme} label={t.muniName} value={editForm.name} onChange={v=>setEditForm(p=>({...p,name:v}))}/>
              <Field theme={theme} label={t.city} value={editForm.city} onChange={v=>setEditForm(p=>({...p,city:v}))}/>
              <div style={{display:'flex',gap:8}}>
                <button style={S.btn('#22c55e')} onClick={saveEdit} disabled={loading}>{loading?t.loading:t.save}</button>
                <button style={S.btn('#374151')} onClick={()=>setEditingId(null)}>{t.cancel}</button>
              </div>
            </div>
          )}
          {expanded===m.id&&<MuniDetails id={m.id} reload={load} theme={theme} S={S} t={t} lang={lang} user={user}/>}
        </div>
      ))}
    </div>
  );
}

function SiteChecklist({theme,lang,sites,selectedIds,onToggle}) {
  return (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:12,color:theme.subtext,marginBottom:6,fontWeight:700}}>
        {lang==='he'?'אתרים משוייכים':'Assigned sites'}
        <span style={{fontWeight:400}}> — {lang==='he'?'אפשר לבחור כמה שצריך':'select as many as needed'}</span>
      </div>
      <div style={{maxHeight:180,overflowY:'auto',border:`1px solid ${theme.border}`,borderRadius:6,padding:'6px 10px',background:theme.inputBg}}>
        {(!sites||sites.length===0) && <div style={{color:theme.subtext,fontSize:12,padding:'4px 0'}}>{lang==='he'?'אין אתרים ללקוח זה עדיין':'No sites for this client yet'}</div>}
        {(sites||[]).map(s=>(
          <label key={s.id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',fontSize:13,color:theme.text,cursor:'pointer'}}>
            <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={e=>onToggle(s.id,e.target.checked)}/>
            {s.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function SiteFloorplansPanel({site,theme,S,t,lang}) {
  const [floorplans,setFloorplans]=useState([]);
  const [buttons,setButtons]=useState([]);
  const [devices,setDevices]=useState([]);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState('');
  const [newFpLabel,setNewFpLabel]=useState('');
  const [newFpFile,setNewFpFile]=useState(null);
  const [showBtnForm,setShowBtnForm]=useState(false);
  const emptyBtn={label:'',button_type:'mushroom',floor:'',room:'',floorplan_id:'',pin_x:null,pin_y:null,device_id:'',di_channel:''};
  const [btnForm,setBtnForm]=useState(emptyBtn);
  const [editingBtnId,setEditingBtnId]=useState(null);

  const load=async()=>{
    try{
      const [fps,btns,devs]=await Promise.all([api.get(`/sites/${site.id}/floorplans`),api.get(`/sites/${site.id}/buttons`),api.get(`/sites/${site.id}/devices`)]);
      setFloorplans(fps);setButtons(btns);setDevices(devs);
    }catch(e){}
  };
  useEffect(()=>{load();},[site.id]);

  const uploadFloorplan=async()=>{
    if(!newFpLabel.trim()){setErr(lang==='he'?'יש להזין שם לתרשים':'Please enter a name for the floor plan');return;}
    if(!newFpFile){setErr(lang==='he'?'יש לבחור תמונה':'Please choose an image');return;}
    setLoading(true);setErr('');
    try{
      const fd=new FormData();
      fd.append('label',newFpLabel);
      fd.append('image',newFpFile);
      await api.post(`/sites/${site.id}/floorplans`,fd);
      setNewFpLabel('');setNewFpFile(null);
      load();
    }catch(e){setErr(e.message);}
    setLoading(false);
  };

  const deleteFloorplan=async(fid,label)=>{
    if(!window.confirm(`${t.delete} "${label}"?`))return;
    try{await api.delete(`/floorplans/${fid}`);load();}catch(e){alert(e.message);}
  };

  const startNewButton=()=>{setEditingBtnId(null);setBtnForm(emptyBtn);setShowBtnForm(true);setErr('');};
  const startEditButton=(b)=>{
    setEditingBtnId(b.id);
    setBtnForm({label:b.label||'',button_type:b.button_type,floor:b.floor||'',room:b.room||'',
      floorplan_id:b.floorplan_id||'',pin_x:b.pin_x!=null?parseFloat(b.pin_x):null,pin_y:b.pin_y!=null?parseFloat(b.pin_y):null,
      device_id:b.device_id||'',di_channel:b.di_channel!=null?String(b.di_channel):''});
    setShowBtnForm(true);setErr('');
  };

  const saveButton=async()=>{
    if(!btnForm.label.trim()){setErr(lang==='he'?'יש להזין שם לכפתור':'Please enter a button name');return;}
    if(btnForm.floorplan_id && (btnForm.pin_x==null||btnForm.pin_y==null)){
      setErr(lang==='he'?'יש ללחוץ על התרשים כדי לסמן את מיקום הכפתור':'Please click on the floor plan to set the button location');
      return;
    }
    if(btnForm.device_id && !btnForm.di_channel){
      setErr(lang==='he'?'בחרת מכשיר - יש גם לבחור ערוץ (1 או 2)':'A device is selected - please also pick a channel (1 or 2)');
      return;
    }
    setLoading(true);setErr('');
    try{
      const payload={...btnForm,floorplan_id:btnForm.floorplan_id||null,device_id:btnForm.device_id||null,di_channel:btnForm.di_channel?parseInt(btnForm.di_channel,10):null};
      if(editingBtnId) await api.put(`/buttons/${editingBtnId}`,{...payload,is_active:true});
      else await api.post(`/sites/${site.id}/buttons`,payload);
      setShowBtnForm(false);load();
    }catch(e){setErr(e.message);}
    setLoading(false);
  };

  const deleteButton=async(bid,label)=>{
    if(!window.confirm(`${t.delete} "${label}"?`))return;
    try{await api.delete(`/buttons/${bid}`);load();}catch(e){alert(e.message);}
  };

  const selectedFp=floorplans.find(f=>f.id===btnForm.floorplan_id);
  const onImageClick=(e)=>{
    const rect=e.currentTarget.getBoundingClientRect();
    const x=(e.clientX-rect.left)/rect.width, y=(e.clientY-rect.top)/rect.height;
    setBtnForm(p=>({...p,pin_x:Math.max(0,Math.min(1,x)),pin_y:Math.max(0,Math.min(1,y))}));
  };

  return (
    <div style={{padding:16,borderTop:`1px solid ${theme.border}`,background:theme.inputBg}}>
      {err&&<div style={{color:'#ef4444',marginBottom:10,fontSize:13}}>{err}</div>}

      <div style={{fontWeight:700,fontSize:13,color:theme.text,marginBottom:8}}>🖼 {lang==='he'?'תרשימי מבנה':'Building floor plans'}</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:10,marginBottom:12}}>
        {floorplans.map(fp=>(
          <div key={fp.id} style={{border:`1px solid ${theme.border}`,borderRadius:8,padding:8,textAlign:'center',width:120}}>
            <img src={fp.image_path} alt={fp.label} style={{width:'100%',height:80,objectFit:'cover',borderRadius:4,marginBottom:6}}/>
            <div style={{fontSize:12,color:theme.text,fontWeight:600,marginBottom:4}}>{fp.label}</div>
            <button onClick={()=>deleteFloorplan(fp.id,fp.label)} style={{...S.deleteBtn,fontSize:11,padding:'2px 8px'}}>{t.delete}</button>
          </div>
        ))}
        {floorplans.length===0&&<div style={{color:theme.subtext,fontSize:12}}>{lang==='he'?'אין עדיין תרשימים לאתר זה':'No floor plans for this site yet'}</div>}
      </div>
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
        <input placeholder={lang==='he'?'שם התרשים (למשל קומה א)':'Floor plan name (e.g. Floor 1)'} value={newFpLabel} onChange={e=>setNewFpLabel(e.target.value)}
          style={{flex:'1 1 160px',background:theme.card,border:`1px solid ${theme.border}`,borderRadius:6,padding:'7px 10px',color:theme.text,fontSize:13}}/>
        <input type="file" accept="image/*" onChange={e=>setNewFpFile(e.target.files[0])} style={{fontSize:12,color:theme.text}}/>
        <button style={S.btn('#3b82f6')} onClick={uploadFloorplan} disabled={loading}>{loading?t.loading:(lang==='he'?'העלה תרשים':'Upload plan')}</button>
      </div>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <div style={{fontWeight:700,fontSize:13,color:theme.text}}>🔴 {lang==='he'?'כפתורים':'Buttons'}</div>
        <button style={S.btn('#f97316')} onClick={()=>{showBtnForm?setShowBtnForm(false):startNewButton();}}>
          {showBtnForm?t.cancel:(lang==='he'?'+ הוסף כפתור':'+ Add button')}
        </button>
      </div>

      {showBtnForm&&(
        <div style={{background:theme.card,border:`1px solid ${theme.border}`,borderRadius:8,padding:12,marginBottom:12}}>
          <Field theme={theme} label={lang==='he'?'שם הכפתור':'Button name'} value={btnForm.label} onChange={v=>setBtnForm(p=>({...p,label:v}))}/>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{lang==='he'?'סוג כפתור':'Button type'}</div>
            <select value={btnForm.button_type} onChange={e=>setBtnForm(p=>({...p,button_type:e.target.value}))}
              style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14}}>
              <option value="mushroom">{lang==='he'?'פטריה':'Mushroom'}</option>
              <option value="hidden">{lang==='he'?'נסתר':'Hidden'}</option>
            </select>
          </div>
          <div style={{display:'flex',gap:8}}>
            <div style={{flex:1}}><Field theme={theme} label={lang==='he'?'קומה (טקסט, לא חובה)':'Floor (text, optional)'} value={btnForm.floor} onChange={v=>setBtnForm(p=>({...p,floor:v}))}/></div>
            <div style={{flex:1}}><Field theme={theme} label={lang==='he'?'חדר (לא חובה)':'Room (optional)'} value={btnForm.room} onChange={v=>setBtnForm(p=>({...p,room:v}))}/></div>
          </div>

          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>
              {lang==='he'?'מכשיר מקושר (לא חובה - רק ללחצנים מהדגם הגנרי החדש)':'Linked device (optional - only for the new generic-firmware buttons)'}
            </div>
            <div style={{display:'flex',gap:8}}>
              <select value={btnForm.device_id} onChange={e=>setBtnForm(p=>({...p,device_id:e.target.value}))}
                style={{flex:2,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,fontSize:14}}>
                <option value="">{lang==='he'?'ללא מכשיר (כתובת MQTT ידנית)':'No device (manual MQTT topic)'}</option>
                {devices.map(d=><option key={d.id} value={d.id}>{d.label||d.mqtt_client_id}</option>)}
              </select>
              <select value={btnForm.di_channel} onChange={e=>setBtnForm(p=>({...p,di_channel:e.target.value}))}
                style={{flex:1,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,fontSize:14}}>
                <option value="">{lang==='he'?'ערוץ':'Channel'}</option>
                <option value="1">1 (GPIO27)</option>
                <option value="2">2 (GPIO26)</option>
              </select>
            </div>
            {devices.length===0&&<div style={{fontSize:12,color:theme.subtext,marginTop:4}}>
              {lang==='he'?'אין עדיין מכשירים משוייכים לאתר זה - שייך מכשיר מהלשונית "מכשירים" קודם':'No devices assigned to this site yet - assign one from the "Devices" tab first'}
            </div>}
          </div>

          {floorplans.length>0&&(
            <div style={{marginTop:6,marginBottom:6}}>
              <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{lang==='he'?'מיקום על תרשים (לא חובה)':'Position on floor plan (optional)'}</div>
              <select value={btnForm.floorplan_id} onChange={e=>setBtnForm(p=>({...p,floorplan_id:e.target.value,pin_x:null,pin_y:null}))}
                style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14,marginBottom:8}}>
                <option value="">{lang==='he'?'ללא מיקום על תרשים':'No floor plan position'}</option>
                {floorplans.map(fp=><option key={fp.id} value={fp.id}>{fp.label}</option>)}
              </select>
              {selectedFp&&(
                <div>
                  <div style={{fontSize:13,color:'#f97316',marginBottom:8,fontWeight:700}}>
                    👆 {lang==='he'?'לחץ בדיוק על המקום הרצוי בתמונה למטה כדי למקם את הכפתור':'Click exactly where you want on the image below to place the button'}
                  </div>
                  <div onClick={onImageClick} style={{position:'relative',display:'block',width:'100%',cursor:'crosshair',
                    border:`2px solid ${btnForm.pin_x!=null?'#22c55e':theme.border}`,borderRadius:8,overflow:'hidden',background:'#000'}}>
                    <img src={selectedFp.image_path} alt={selectedFp.label} style={{display:'block',width:'100%',maxHeight:'70vh',objectFit:'contain',margin:'0 auto'}}/>
                    {btnForm.pin_x!=null&&btnForm.pin_y!=null&&(
                      <div style={{position:'absolute',left:`${btnForm.pin_x*100}%`,top:`${btnForm.pin_y*100}%`,transform:'translate(-50%,-50%)',
                        width:28,height:28,borderRadius:'50%',background:'#ef4444',border:'3px solid #fff',boxShadow:'0 0 10px rgba(0,0,0,0.6)',
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>📍</div>
                    )}
                  </div>
                  <div style={{fontSize:12,marginTop:6,color:btnForm.pin_x!=null?'#22c55e':theme.subtext,fontWeight:600}}>
                    {btnForm.pin_x!=null
                      ? (lang==='he'?`✓ מיקום נבחר - אפשר ללחוץ שוב במקום אחר כדי לשנות`:'✓ Location set - click again elsewhere to change it')
                      : (lang==='he'?'טרם נבחר מיקום':'No location set yet')}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{display:'flex',gap:8,marginTop:10}}>
            <button style={S.btn('#22c55e')} onClick={saveButton} disabled={loading}>{loading?t.loading:t.save}</button>
            <button style={S.btn('#374151')} onClick={()=>setShowBtnForm(false)}>{t.cancel}</button>
          </div>
        </div>
      )}

      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead><tr style={{color:theme.subtext}}>
          <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'שם':'Name'}</th>
          <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'סוג':'Type'}</th>
          <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'קומה/חדר':'Floor/Room'}</th>
          <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'תרשים':'Floor plan'}</th>
          <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'מכשיר':'Device'}</th>
          <th></th>
        </tr></thead>
        <tbody>{buttons.map(b=>(
          <tr key={b.id} style={{borderTop:`1px solid ${theme.border}`}}>
            <td style={{padding:'8px',color:theme.text}}>{b.label}</td>
            <td style={{padding:'8px',color:theme.subtext}}>{b.button_type==='mushroom'?(lang==='he'?'פטריה':'Mushroom'):(lang==='he'?'נסתר':'Hidden')}</td>
            <td style={{padding:'8px',color:theme.subtext}}>{[b.floor,b.room].filter(Boolean).join(' / ')||'-'}</td>
            <td style={{padding:'8px',color:theme.subtext}}>{b.floorplan_id?'📍 '+(floorplans.find(f=>f.id===b.floorplan_id)?.label||'-'):'-'}</td>
            <td style={{padding:'8px',color:theme.subtext}}>{b.device_id?`📡 ${b.device_label||'-'} / ch${b.di_channel??'?'}`:'-'}</td>
            <td style={{padding:'8px',display:'flex',gap:6}}>
              <button style={{...S.deleteBtn,color:'#3b82f6',borderColor:'#3b82f6'}} onClick={()=>startEditButton(b)}>✏️</button>
              <button style={S.deleteBtn} onClick={()=>deleteButton(b.id,b.label)}>🗑</button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {buttons.length===0&&<div style={{color:theme.subtext,fontSize:12,marginTop:8}}>{lang==='he'?'אין עדיין כפתורים לאתר זה':'No buttons for this site yet'}</div>}
    </div>
  );
}

function MuniDetails({id,reload,theme,S,t,lang,user}) {
  const [data,setData]=useState(null);
  const [activeTab,setActiveTab]=useState('sms');
  const [showSite,setShowSite]=useState(false);
  const [showUser,setShowUser]=useState(false);
  const [showSms,setShowSms]=useState(false);
  const [siteForm,setSiteForm]=useState({name:'',site_type:'school',address:'',city:'',entry_code:'',site_notes:'',contacts:[]});
  const [userForm,setUserForm]=useState({full_name:'',email:'',phone:'',password:'',role:'moked_operator',site_ids:[]});
  const [smsForm,setSmsForm]=useState({full_name:'',phone:'',role_label:''});
  const [editingSiteId,setEditingSiteId]=useState(null);
  const [editingUserId,setEditingUserId]=useState(null);
  const [editUserForm,setEditUserForm]=useState({full_name:'',phone:'',role:'moked_operator',site_ids:[]});
  const [editingSmsId,setEditingSmsId]=useState(null);
  const [expandedFpSiteId,setExpandedFpSiteId]=useState(null);
  const [loading,setLoading]=useState(false);

  const load=async()=>{ try{const d=await api.get(`/municipalities/${id}`);setData(d);}catch(e){} };
  useEffect(()=>{load();},[id]);

  const addSite=async()=>{ setLoading(true); try{await api.post(`/municipalities/${id}/sites`,siteForm);setShowSite(false);load();reload();}catch(e){alert(e.message);} setLoading(false); };
  const addUser=async()=>{ setLoading(true); try{await api.post(`/municipalities/${id}/users`,userForm);setShowUser(false);load();reload();}catch(e){alert(e.message);} setLoading(false); };
  const addSms=async()=>{ setLoading(true); try{await api.post(`/municipalities/${id}/sms-recipients`,smsForm);setShowSms(false);load();}catch(e){alert(e.message);} setLoading(false); };
  const deleteSite=async(sid,name)=>{ if(!window.confirm(`${t.delete} "${name}"?`))return; try{await api.delete(`/municipalities/${id}/sites/${sid}`);load();reload();}catch(e){alert(e.message);} };
  const deleteUser=async(uid,name)=>{ if(!window.confirm(`${t.delete} "${name}"?`))return; try{await api.delete(`/municipalities/${id}/users/${uid}`);load();reload();}catch(e){alert(e.message);} };
  const deleteSms=async(rid,name)=>{ if(!window.confirm(`${t.delete} "${name}"?`))return; try{await api.delete(`/municipalities/${id}/sms-recipients/${rid}`);load();}catch(e){alert(e.message);} };

  const startEditSite=(s)=>{
    setEditingSiteId(s.id);
    setSiteForm({name:s.name||'',site_type:s.site_type||'school',address:s.address||'',city:s.city||'',entry_code:s.entry_code||'',site_notes:s.site_notes||'',contacts:(s.contacts||[]).map(c=>({full_name:c.full_name||'',phone:c.phone||'',role_label:c.role_label||'',email:c.email||''}))});
    setShowSite(true);
  };
  const saveEditSite=async()=>{ setLoading(true); try{await api.put(`/municipalities/${id}/sites/${editingSiteId}`,siteForm);setShowSite(false);setEditingSiteId(null);load();reload();}catch(e){alert(e.message);} setLoading(false); };
  const cancelSiteForm=()=>{ setShowSite(false); setEditingSiteId(null); setSiteForm({name:'',site_type:'school',address:'',city:'',entry_code:'',site_notes:'',contacts:[]}); };

  const addContactRow=()=>setSiteForm(p=>({...p,contacts:[...p.contacts,{full_name:'',phone:'',role_label:'',email:''}]}));
  const updateContactRow=(i,field,val)=>setSiteForm(p=>({...p,contacts:p.contacts.map((c,ci)=>ci===i?{...c,[field]:val}:c)}));
  const removeContactRow=(i)=>setSiteForm(p=>({...p,contacts:p.contacts.filter((_,ci)=>ci!==i)}));

  const startEditUser=(u)=>{ setEditingUserId(u.id); setEditUserForm({full_name:u.full_name||'',phone:u.phone||'',role:u.role||'moked_operator',site_ids:(u.sites||[]).map(s=>s.id)}); };
  const saveEditUser=async()=>{ setLoading(true); try{await api.put(`/municipalities/${id}/users/${editingUserId}`,editUserForm);setEditingUserId(null);load();reload();}catch(e){alert(e.message);} setLoading(false); };

  const startEditSms=(r)=>{ setEditingSmsId(r.id); setSmsForm({full_name:r.full_name||'',phone:r.phone||'',role_label:r.role_label||''}); setShowSms(true); };
  const saveEditSms=async()=>{ setLoading(true); try{await api.put(`/municipalities/${id}/sms-recipients/${editingSmsId}`,smsForm);setShowSms(false);setEditingSmsId(null);load();}catch(e){alert(e.message);} setLoading(false); };
  const cancelSmsForm=()=>{ setShowSms(false); setEditingSmsId(null); setSmsForm({full_name:'',phone:'',role_label:''}); };

  if(!data)return<div style={{padding:16,color:theme.subtext}}>{t.loading}</div>;

  const subTabs=[['sms',`📱 ${t.smsRecipients}`],['sites',`🏗 ${t.sites}`],['users',`👤 ${t.users}`]];

  return (
    <div style={{borderTop:`1px solid ${theme.border}`,padding:16}}>
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        {subTabs.map(([tv,l])=>(
          <button key={tv} style={{background:activeTab===tv?theme.navActive:'rgba(255,255,255,0.3)',color:theme.text,border:`1px solid ${theme.border}`,borderRadius:6,padding:'6px 14px',cursor:'pointer',fontSize:13}} onClick={()=>setActiveTab(tv)}>
            {l} ({tv==='sms'?data.sms_recipients?.length||0:tv==='sites'?data.sites?.length||0:data.users?.length||0})
          </button>
        ))}
      </div>

      {activeTab==='sms'&&(
        <div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
            <div style={{fontSize:13,color:theme.subtext}}>{t.smsRecipients}</div>
            <button style={S.btn('#f97316')} onClick={()=>{ if(showSms){cancelSmsForm();}else{setShowSms(true);} }}>{t.addRecipient}</button>
          </div>
          {showSms&&(
            <div style={{background:theme.inputBg,borderRadius:8,padding:12,marginBottom:12,border:`1px solid ${theme.border}`}}>
              {editingSmsId&&<div style={{fontSize:12,color:'#3b82f6',marginBottom:8,fontWeight:700}}>✏️ {lang==='he'?'עריכת נמען':'Editing recipient'}</div>}
              <Field theme={theme} label={t.name} value={smsForm.full_name} onChange={v=>setSmsForm(p=>({...p,full_name:v}))}/>
              <Field theme={theme} label={t.phone} value={smsForm.phone} onChange={v=>setSmsForm(p=>({...p,phone:v}))}/>
              <Field theme={theme} label={t.role} value={smsForm.role_label} onChange={v=>setSmsForm(p=>({...p,role_label:v}))}/>
              <div style={{display:'flex',gap:8}}>
                <button style={S.btn('#22c55e')} onClick={editingSmsId?saveEditSms:addSms} disabled={loading}>{loading?t.loading:t.save}</button>
                <button style={S.btn('#374151')} onClick={cancelSmsForm}>{t.cancel}</button>
              </div>
            </div>
          )}
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{color:theme.subtext}}>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{t.name}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{t.phone}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{t.role}</th>
              <th></th>
            </tr></thead>
            <tbody>{data.sms_recipients?.map(r=>(
              <tr key={r.id} style={{borderTop:`1px solid ${theme.border}`}}>
                <td style={{padding:'8px',color:theme.text}}>{r.full_name}</td>
                <td style={{padding:'8px',color:theme.text}}>{r.phone}</td>
                <td style={{padding:'8px',color:theme.subtext}}>{r.role_label||'-'}</td>
                <td style={{padding:'8px',display:'flex',gap:6}}>
                  <button style={{...S.deleteBtn,color:'#3b82f6',borderColor:'#3b82f6'}} onClick={()=>startEditSms(r)}>✏️ {lang==='he'?'עריכה':'Edit'}</button>
                  <button style={S.deleteBtn} onClick={()=>deleteSms(r.id,r.full_name)}>{t.remove}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {activeTab==='sites'&&(
        <div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
            <div style={{fontSize:13,color:theme.subtext}}>{t.sites}</div>
            <button style={S.btn('#3b82f6')} onClick={()=>{ if(showSite){cancelSiteForm();}else{setShowSite(true);} }}>{t.newSite}</button>
          </div>
          {showSite&&(
            <div style={{background:theme.inputBg,borderRadius:8,padding:12,marginBottom:12,border:`1px solid ${theme.border}`}}>
              {editingSiteId&&<div style={{fontSize:12,color:'#3b82f6',marginBottom:8,fontWeight:700}}>✏️ {lang==='he'?'עריכת אתר':'Editing site'}</div>}
              <Field theme={theme} label={t.siteName} value={siteForm.name} onChange={v=>setSiteForm(p=>({...p,name:v}))}/>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{lang==='he'?'סוג אתר':'Site Type'}</div>
                <select style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14}} value={siteForm.site_type} onChange={e=>setSiteForm(p=>({...p,site_type:e.target.value}))}>
                  <option value="school">{lang==='he'?'בית ספר':'School'}</option>
                  <option value="kindergarten">{lang==='he'?'גן ילדים':'Kindergarten'}</option>
                  <option value="other">{lang==='he'?'אחר':'Other'}</option>
                </select>
              </div>
              <Field theme={theme} label={t.address} value={siteForm.address} onChange={v=>setSiteForm(p=>({...p,address:v}))}/>
              <Field theme={theme} label={t.city} value={siteForm.city} onChange={v=>setSiteForm(p=>({...p,city:v}))}/>
              <Field theme={theme} label={lang==='he'?'קוד כניסה':'Entry Code'} value={siteForm.entry_code} onChange={v=>setSiteForm(p=>({...p,entry_code:v}))}/>
              <Field theme={theme} label={lang==='he'?'הערות':'Notes'} value={siteForm.site_notes} onChange={v=>setSiteForm(p=>({...p,site_notes:v}))}/>

              <div style={{marginTop:6,marginBottom:10}}>
                <div style={{fontSize:12,color:theme.subtext,marginBottom:6,fontWeight:700}}>
                  {lang==='he'?'אנשי קשר באתר':'Site contacts'}
                  <span style={{fontWeight:400}}> — {lang==='he'?'אפשר להוסיף כמה שצריך, אם אחד לא זמין למוקדן יהיה עם מי לדבר. אימייל (לא חובה) — אם ממולא, איש הקשר יקבל מייל אוטומטי בכל התראה באתר':'add as many as needed, so the operator always has someone to call. Email (optional) — if filled, the contact gets an automatic alert email'}</span>
                </div>
                {siteForm.contacts.map((c,i)=>(
                  <div key={i} style={{display:'flex',gap:6,marginBottom:6,alignItems:'center',flexWrap:'wrap'}}>
                    <input placeholder={lang==='he'?'שם':'Name'} value={c.full_name} onChange={e=>updateContactRow(i,'full_name',e.target.value)}
                      style={{flex:2,minWidth:100,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'7px 10px',color:theme.text,fontSize:13}}/>
                    <input placeholder={lang==='he'?'טלפון':'Phone'} value={c.phone} onChange={e=>updateContactRow(i,'phone',e.target.value)}
                      style={{flex:2,minWidth:100,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'7px 10px',color:theme.text,fontSize:13}}/>
                    <input placeholder={lang==='he'?'תפקיד (לא חובה)':'Role (optional)'} value={c.role_label} onChange={e=>updateContactRow(i,'role_label',e.target.value)}
                      style={{flex:2,minWidth:100,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'7px 10px',color:theme.text,fontSize:13}}/>
                    <input placeholder={lang==='he'?'אימייל (לא חובה)':'Email (optional)'} type="email" value={c.email||''} onChange={e=>updateContactRow(i,'email',e.target.value)}
                      style={{flex:2,minWidth:140,background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'7px 10px',color:theme.text,fontSize:13}}/>
                    <button onClick={()=>removeContactRow(i)} title={lang==='he'?'הסר':'Remove'}
                      style={{background:'transparent',border:`1px solid ${theme.border}`,color:'#ef4444',borderRadius:6,padding:'6px 10px',cursor:'pointer',fontSize:13,flexShrink:0}}>✕</button>
                  </div>
                ))}
                <button onClick={addContactRow} style={{...S.btn('#3b82f6'),fontSize:12,padding:'6px 12px'}}>+ {lang==='he'?'הוסף איש קשר':'Add contact'}</button>
              </div>

              <div style={{display:'flex',gap:8}}>
                <button style={S.btn('#22c55e')} onClick={editingSiteId?saveEditSite:addSite} disabled={loading}>{loading?t.loading:t.save}</button>
                <button style={S.btn('#374151')} onClick={cancelSiteForm}>{t.cancel}</button>
              </div>
            </div>
          )}
          {data.sites?.length===0&&<div style={{color:theme.subtext,fontSize:13}}>{t.noSites}</div>}
          {data.sites?.map(s=>(
            <div key={s.id} style={{...S.card,marginBottom:8}}>
              <div style={{padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <span style={{fontWeight:700,color:theme.cardText}}>{s.name}</span>
                  <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>{s.address}, {s.city}</span>
                  <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>👤 {s.contacts?.length||0} {lang==='he'?'אנשי קשר':'contacts'}</span>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button style={{...S.deleteBtn,color:'#8b5cf6',borderColor:'#8b5cf6'}} onClick={()=>setExpandedFpSiteId(expandedFpSiteId===s.id?null:s.id)}>🖼 {lang==='he'?'תרשימים וכפתורים':'Floor plans & buttons'}</button>
                  <button style={{...S.deleteBtn,color:'#3b82f6',borderColor:'#3b82f6'}} onClick={()=>startEditSite(s)}>✏️ {lang==='he'?'עריכה':'Edit'}</button>
                  <button style={S.deleteBtn} onClick={()=>deleteSite(s.id,s.name)}>🗑 {t.delete}</button>
                </div>
              </div>
              {expandedFpSiteId===s.id&&<SiteFloorplansPanel site={s} theme={theme} S={S} t={t} lang={lang}/>}
            </div>
          ))}
        </div>
      )}

      {activeTab==='users'&&(
        <div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
            <div style={{fontSize:13,color:theme.subtext}}>{t.users}</div>
            <button style={S.btn('#8b5cf6')} onClick={()=>setShowUser(!showUser)}>{t.newUser}</button>
          </div>
          {showUser&&(
            <div style={{background:theme.inputBg,borderRadius:8,padding:12,marginBottom:12,border:`1px solid ${theme.border}`}}>
              <Field theme={theme} label={t.name} value={userForm.full_name} onChange={v=>setUserForm(p=>({...p,full_name:v}))}/>
              <Field theme={theme} label={t.email} value={userForm.email} onChange={v=>setUserForm(p=>({...p,email:v}))} type="email"/>
              <Field theme={theme} label={t.phone} value={userForm.phone} onChange={v=>setUserForm(p=>({...p,phone:v}))}/>
              <Field theme={theme} label={t.password} value={userForm.password} onChange={v=>setUserForm(p=>({...p,password:v}))} type="password"/>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{t.role}</div>
                <select style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14}} value={userForm.role} onChange={e=>setUserForm(p=>({...p,role:e.target.value}))}>
                  <option value="moked_operator">{roleLabel('moked_operator',lang)}</option>
                  <option value="site_manager">{roleLabel('site_manager',lang)}</option>
                  <option value="cross_municipal">{roleLabel('cross_municipal',lang)}</option>
                  {user?.role==='superadmin' && <option value="admin">{roleLabel('admin',lang)}</option>}
                </select>
              </div>
              {userForm.role==='site_manager' && (
                <SiteChecklist theme={theme} lang={lang} sites={data.sites} selectedIds={userForm.site_ids}
                  onToggle={(sid,checked)=>setUserForm(p=>({...p,site_ids:checked?[...p.site_ids,sid]:p.site_ids.filter(id=>id!==sid)}))}/>
              )}
              <div style={{display:'flex',gap:8}}>
                <button style={S.btn('#22c55e')} onClick={addUser} disabled={loading}>{loading?t.loading:t.save}</button>
                <button style={S.btn('#374151')} onClick={()=>setShowUser(false)}>{t.cancel}</button>
              </div>
            </div>
          )}
          {data.users?.length===0&&<div style={{color:theme.subtext,fontSize:13}}>{t.noUsers}</div>}
          {data.users?.map(u=>(
            <div key={u.id} style={{...S.card,marginBottom:8}}>
              <div style={{padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <span style={{fontWeight:700,color:theme.cardText}}>{u.full_name}</span>
                  <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>{u.email}</span>
                  <span style={{background:roleBadgeColors(u.role,theme)[0],border:`1px solid ${roleBadgeColors(u.role,theme)[1]}`,borderRadius:12,padding:'2px 10px',fontSize:12,color:'#fff',marginRight:8}}>
  {roleLabel(u.role,lang)}
</span>
                  {u.role==='site_manager' && <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>📍 {u.sites?.length||0} {lang==='he'?'אתרים':'sites'}</span>}
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button style={{...S.deleteBtn,color:'#3b82f6',borderColor:'#3b82f6'}} onClick={()=>startEditUser(u)}>✏️ {lang==='he'?'עריכה':'Edit'}</button>
                  <button style={S.deleteBtn} onClick={()=>deleteUser(u.id,u.full_name)}>🗑 {t.delete}</button>
                </div>
              </div>
              {editingUserId===u.id&&(
                <div style={{padding:12,borderTop:`1px solid ${theme.border}`,background:theme.inputBg}}>
                  <Field theme={theme} label={t.name} value={editUserForm.full_name} onChange={v=>setEditUserForm(p=>({...p,full_name:v}))}/>
                  <Field theme={theme} label={t.phone} value={editUserForm.phone} onChange={v=>setEditUserForm(p=>({...p,phone:v}))}/>
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:12,color:theme.subtext,marginBottom:4}}>{t.role}</div>
                    <select style={{background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'8px 12px',color:theme.text,width:'100%',fontSize:14}} value={editUserForm.role} onChange={e=>setEditUserForm(p=>({...p,role:e.target.value}))}>
                      <option value="moked_operator">{roleLabel('moked_operator',lang)}</option>
                      <option value="site_manager">{roleLabel('site_manager',lang)}</option>
                      <option value="cross_municipal">{roleLabel('cross_municipal',lang)}</option>
                      {user?.role==='superadmin' && <option value="admin">{roleLabel('admin',lang)}</option>}
                    </select>
                  </div>
                  {editUserForm.role==='site_manager' && (
                    <SiteChecklist theme={theme} lang={lang} sites={data.sites} selectedIds={editUserForm.site_ids}
                      onToggle={(sid,checked)=>setEditUserForm(p=>({...p,site_ids:checked?[...p.site_ids,sid]:p.site_ids.filter(id=>id!==sid)}))}/>
                  )}
                  <div style={{display:'flex',gap:8}}>
                    <button style={S.btn('#22c55e')} onClick={saveEditUser} disabled={loading}>{loading?t.loading:t.save}</button>
                    <button style={S.btn('#374151')} onClick={()=>setEditingUserId(null)}>{t.cancel}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SitesTab({theme,S,t}) {
  const [sites,setSites]=useState([]);
  useEffect(()=>{api.get('/sites').then(setSites).catch(()=>{});},[]);
  return (
    <div>
      <h2 style={{color:theme.text}}>{t.sites}</h2>
      <div style={{color:theme.subtext,fontSize:13,marginBottom:16}}>{sites.length} {t.active}</div>
      {sites.map(s=>(
        <div key={s.id} style={{...S.card,padding:'10px 16px',marginBottom:8,display:'flex',justifyContent:'space-between'}}>
          <div>
            <span style={{fontWeight:700,color:theme.cardText}}>{s.name}</span>
            <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>{s.municipality} — {s.city}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({theme,S,t}) {
  const [alerts,setAlerts]=useState([]);
  useEffect(()=>{api.get('/alerts/history').then(setAlerts).catch(()=>{});},[]);
  return (
    <div>
      <h2 style={{color:theme.text}}>{t.history}</h2>
      <div style={{color:theme.subtext,fontSize:13,marginBottom:16}}>{alerts.length} {t.events}</div>
      {alerts.map(a=>(
        <div key={a.id} style={{...S.card,padding:'10px 16px',marginBottom:8}}>
          <span style={{color:theme.subtext,fontSize:12}}>{new Date(a.created_at).toLocaleString('he-IL')}</span>
          <span style={{marginRight:12,color:theme.text}}>{a.site_name}</span>
        </div>
      ))}
    </div>
  );
}

function DevicesTab({theme,S,t,lang,user}) {
  const [unassigned,setUnassigned]=useState([]);
  const [allDevices,setAllDevices]=useState([]);
  const [sites,setSites]=useState([]);
  const [assignForm,setAssignForm]=useState({}); // deviceId -> {site_id,label}
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState('');
  const [releases,setReleases]=useState([]);
  const [otaVersion,setOtaVersion]=useState('');
  const [otaNotes,setOtaNotes]=useState('');
  const [otaFile,setOtaFile]=useState(null);
  const [otaBusy,setOtaBusy]=useState(false);
  const [otaMsg,setOtaMsg]=useState('');

  const isSuperadmin = user?.role==='superadmin';

  const load=async()=>{
    try{
      const calls=[api.get('/devices/unassigned'),api.get('/sites')];
      if(isSuperadmin) calls.push(api.get('/devices'), api.get('/ota/releases'));
      const results=await Promise.all(calls);
      setUnassigned(results[0]); setSites(results[1]);
      if(isSuperadmin){ setAllDevices(results[2]); setReleases(results[3]); }
    }catch(e){ if(e?.response?.status!==403) setErr(e.message); }
  };
  useEffect(()=>{load();},[]);

  const assign=async(deviceId)=>{
    const f=assignForm[deviceId]||{};
    if(!f.site_id){ setErr(lang==='he'?'יש לבחור אתר':'Please choose a site'); return; }
    setLoading(true);setErr('');
    try{ await api.put(`/devices/${deviceId}/assign`,{site_id:f.site_id,label:f.label||null}); load(); }
    catch(e){ setErr(e.message); }
    setLoading(false);
  };

  const publishOta=async()=>{
    if(!otaVersion.trim()){ setOtaMsg(lang==='he'?'יש להזין מספר גרסה':'Please enter a version number'); return; }
    if(!otaFile){ setOtaMsg(lang==='he'?'יש לבחור קובץ main.py':'Please choose a main.py file'); return; }
    setOtaBusy(true);setOtaMsg('');
    try{
      const fd=new FormData();
      fd.append('version',otaVersion.trim());
      if(otaNotes.trim()) fd.append('notes',otaNotes.trim());
      fd.append('firmware',otaFile);
      await api.post('/ota/publish',fd);
      setOtaVersion('');setOtaNotes('');setOtaFile(null);
      setOtaMsg(lang==='he'?'הגרסה פורסמה - כל המכשירים יקבלו הודעה לבדוק עדכון':'Version published - every device has been nudged to check for it');
      load();
    }catch(e){ setOtaMsg(e.message); }
    setOtaBusy(false);
  };

  return (
    <div>
      <h2 style={{color:theme.text}}>{lang==='he'?'מכשירים':'Devices'}</h2>
      {err&&<div style={{color:'#ef4444',marginBottom:12}}>{err}</div>}

      <div style={{...S.card,padding:16,marginBottom:20}}>
        <div style={{fontWeight:700,fontSize:14,color:theme.text,marginBottom:6}}>
          📥 {lang==='he'?'מכשירים שאינם משוייכים':'Unassigned devices'}
        </div>
        <div style={{fontSize:12,color:theme.subtext,marginBottom:12}}>
          {lang==='he'
            ?'לוח בקרה גנרי (ESP32) שהודלק לראשונה ודיווח לשרת מופיע כאן, לפי מזהה החומרה שלו - בחר לאיזה אתר הוא שייך. לאחר השיוך, אפשר לחבר אותו לכפתור ספציפי מלשונית האתר (תרשימים וכפתורים).'
            :'A generic (ESP32) board that powered on and reported in for the first time shows up here by its hardware id - pick which site it belongs to. After assigning, link it to a specific button from the site\'s Floor plans & buttons tab.'}
        </div>
        {unassigned.length===0&&<div style={{color:theme.subtext,fontSize:13}}>{lang==='he'?'אין מכשירים ממתינים לשיוך':'No devices waiting to be assigned'}</div>}
        {unassigned.map(d=>(
          <div key={d.id} style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',padding:'8px 0',borderTop:`1px solid ${theme.border}`}}>
            <span style={{fontFamily:'monospace',fontSize:13,color:theme.text}}>{d.mqtt_client_id}</span>
            <span style={{fontSize:11,color:theme.subtext}}>{d.created_at?new Date(d.created_at).toLocaleString('he-IL'):''}</span>
            <select value={(assignForm[d.id]||{}).site_id||''} onChange={e=>setAssignForm(p=>({...p,[d.id]:{...p[d.id],site_id:e.target.value}}))}
              style={{flex:'1 1 160px',background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'6px 10px',color:theme.text,fontSize:13}}>
              <option value="">{lang==='he'?'-- בחר אתר --':'-- choose site --'}</option>
              {sites.map(s=><option key={s.id} value={s.id}>{s.name} ({s.municipality})</option>)}
            </select>
            <input placeholder={lang==='he'?'תווית (לא חובה)':'Label (optional)'} value={(assignForm[d.id]||{}).label||''}
              onChange={e=>setAssignForm(p=>({...p,[d.id]:{...p[d.id],label:e.target.value}}))}
              style={{flex:'1 1 120px',background:theme.inputBg,border:`1px solid ${theme.border}`,borderRadius:6,padding:'6px 10px',color:theme.text,fontSize:13}}/>
            <button style={S.btn('#22c55e')} onClick={()=>assign(d.id)} disabled={loading}>{lang==='he'?'שייך':'Assign'}</button>
          </div>
        ))}
      </div>

      {isSuperadmin&&(
        <div style={{...S.card,padding:16,marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:theme.text,marginBottom:10}}>
            📡 {lang==='he'?'כל המכשירים בשטח':'Fleet overview'}
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{color:theme.subtext}}>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'אתר':'Site'}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'מזהה':'ID'}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'סטטוס':'Status'}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'גרסה':'Version'}</th>
              <th style={{textAlign:lang==='he'?'right':'left',padding:'6px 8px'}}>{lang==='he'?'נראה לאחרונה':'Last seen'}</th>
            </tr></thead>
            <tbody>{allDevices.map(d=>(
              <tr key={d.id} style={{borderTop:`1px solid ${theme.border}`}}>
                <td style={{padding:'8px',color:theme.text}}>{d.site_name||(lang==='he'?'— לא משויך —':'— unassigned —')}</td>
                <td style={{padding:'8px',fontFamily:'monospace',color:theme.subtext}}>{d.label||d.mqtt_client_id}</td>
                <td style={{padding:'8px',color:d.status==='online'?'#22c55e':d.status==='pending'?'#f59e0b':theme.subtext}}>{d.status}</td>
                <td style={{padding:'8px',color:theme.subtext}}>{d.firmware_version||'-'}</td>
                <td style={{padding:'8px',color:theme.subtext}}>{d.last_seen_at?new Date(d.last_seen_at).toLocaleString('he-IL'):'-'}</td>
              </tr>
            ))}</tbody>
          </table>
          {allDevices.length===0&&<div style={{color:theme.subtext,fontSize:13,marginTop:8}}>{lang==='he'?'אין עדיין מכשירים':'No devices yet'}</div>}
        </div>
      )}

      {isSuperadmin&&(
        <div style={{...S.card,padding:16}}>
          <div style={{fontWeight:700,fontSize:14,color:theme.text,marginBottom:6}}>
            🔄 {lang==='he'?'עדכון קושחה (OTA)':'Firmware update (OTA)'}
          </div>
          <div style={{fontSize:12,color:theme.subtext,marginBottom:12}}>
            {lang==='he'
              ?'העלאת קובץ main.py חדש כאן מפרסמת אותו כגרסה הנוכחית ושולחת הודעה מיידית (דרך MQTT) לכל המכשירים בשטח לבדוק אם יש עדכון - אין צורך לגשת לאף מכשיר פיזית.'
              :'Uploading a new main.py here publishes it as the current version and immediately nudges every field device (via MQTT) to check for it - no need to physically visit any unit.'}
          </div>
          {releases[0]&&<div style={{fontSize:13,color:theme.text,marginBottom:12}}>
            {lang==='he'?'גרסה נוכחית: ':'Current version: '}<b>{releases[0].version}</b>
            <span style={{color:theme.subtext}}> ({new Date(releases[0].created_at).toLocaleString('he-IL')})</span>
          </div>}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end',marginBottom:8}}>
            <div style={{flex:'1 1 140px'}}><Field theme={theme} label={lang==='he'?'מספר גרסה':'Version'} value={otaVersion} onChange={setOtaVersion} placeholder="2.1.0"/></div>
            <div style={{flex:'2 1 200px'}}><Field theme={theme} label={lang==='he'?'הערות (לא חובה)':'Notes (optional)'} value={otaNotes} onChange={setOtaNotes}/></div>
            <div style={{flex:'1 1 160px',marginBottom:12}}>
              <input type="file" accept=".py" onChange={e=>setOtaFile(e.target.files[0])} style={{fontSize:12,color:theme.text}}/>
            </div>
          </div>
          <button style={S.btn('#f97316')} onClick={publishOta} disabled={otaBusy}>{otaBusy?t.loading:(lang==='he'?'פרסם עדכון לכולם':'Publish to fleet')}</button>
          {otaMsg&&<div style={{marginTop:8,fontSize:13,color:otaMsg.includes('פורסם')||otaMsg.includes('published')?'#22c55e':'#ef4444'}}>{otaMsg}</div>}
          {releases.length>1&&(
            <div style={{marginTop:16}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.subtext,marginBottom:6}}>{lang==='he'?'היסטוריית גרסאות':'Release history'}</div>
              {releases.slice(1).map(r=>(
                <div key={r.id} style={{fontSize:12,color:theme.subtext,padding:'2px 0'}}>{r.version} — {new Date(r.created_at).toLocaleString('he-IL')} {r.notes?`— ${r.notes}`:''}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UsersTab({theme,S,t,lang}) {
  const [users,setUsers]=useState([]);
  useEffect(()=>{api.get('/users').then(setUsers).catch(()=>{});},[]);
  return (
    <div>
      <h2 style={{color:theme.text}}>{t.users}</h2>
      <div style={{color:theme.subtext,fontSize:13,marginBottom:16}}>{users.length} {t.active}</div>
      {users.map(u=>(
        <div key={u.id} style={{...S.card,padding:'10px 16px',marginBottom:8,display:'flex',justifyContent:'space-between'}}>
          <div>
            <span style={{fontWeight:700,color:theme.cardText}}>{u.full_name}</span>
            <span style={{color:theme.subtext,fontSize:12,marginRight:8}}>{u.email}</span>
            <span style={{background:roleBadgeColors(u.role,theme)[0],border:`1px solid ${roleBadgeColors(u.role,theme)[1]}`,borderRadius:12,padding:'2px 10px',fontSize:12,color:'#fff',marginRight:8}}>
  {roleLabel(u.role,lang)}
</span>
          </div>
        </div>
      ))}
    </div>
  );
}


